import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { listSources, getNodeCatalog } from "@/services/pipeline";
import { listAppRequests } from "@/services/apps";
import { PlusCircle, Server, Database, Link as LinkIcon, Sparkles, Wrench, Plug, Loader2 } from "lucide-react";
import AppRequestDialog from "../components/AppRequestDialog";
import { REQUEST_KIND_META } from "../components/board/requestMeta";

// "Build a source" files an app_request the agent fleet picks up. The two kinds
// are SEPARATE TABS on purpose: whether a brief means "a system we can't reach
// yet" or "the existing connector is wrong" is exactly the judgement an LLM
// classifier got wrong twice in this product (see AppRequestDialog's header).
//
// `changeable` = connectors this project can meaningfully ask to change: the
// ones an agent built (they carry an adapter_archetype) PLUS the ones the
// project's own sources actually run on.
//
// The second half matters. Gating on adapter_archetype alone left this tab
// permanently empty in practice — every agent-built source so far sits on the
// built-in 'External API' type, because the executor is told not to mint a
// near-duplicate kind. So a project full of "AI-built" sources was shown "this
// project has none yet", which reads as a bug. And a change to a built-in
// connector IS a real request: the openFDA nested-array job changed the api
// reader on the shared core engine. Offering only types with no source behind
// them would be the useless half.
function sourceRequestKinds(changeable) {
  return [
    {
      kind: "new_source", label: "New data source", icon: Sparkles,
      hint: REQUEST_KIND_META.new_source.hint,
      titlePlaceholder: "Which system should cortex connect to?",
      detailsPlaceholder:
        "Describe it in plain language — the service or database, its URL or docs if you have them, what records you need, and how you authenticate (API key, username/password…). The agent researches it, builds the connection form, and proves it with a live read before it appears in the library.",
      allowFile: true,
      fileHint: "Optional: API docs, a schema, or a sample export",
    },
    {
      kind: "change_source", label: "Change a data source", icon: Wrench,
      hint: REQUEST_KIND_META.change_source.hint,
      // Pick the connector rather than typing its name — a typo here sends the
      // agent looking for something that doesn't exist.
      textTargets: changeable.map((c) => ({
        value: c.storage_type_out || c.storage_type_in || c.node_type,
        label: c.label || c.storage_type_out || c.storage_type_in,
      })),
      textTargetPrefix: "CONNECTOR",
      targetLabel: "Select a connector…",
      emptyTitle: "No connectors to change yet",
      emptyHint:
        "This tab lists the connectors your sources run on, plus any an agent built for you. This project has no sources yet, so there is nothing to change — add one first.",
      emptyActionKind: "new_source",
      emptyActionLabel: "Build a new data source",
      titlePlaceholder: "What should change about it?",
      detailsPlaceholder:
        "Describe what's wrong or missing — a field the form should collect, a different endpoint or object, authentication that stopped working…",
      allowFile: true,
      fileHint: "Optional: an error message, docs, or a sample response",
    },
  ];
}

import DataSourcesTable from "../components/datasources/DataSourcesTable";
import { StorageInspectModal } from "./Pipeline";
import CreateDataSourceModal from "../components/datasources/CreateDataSourceModal";
import EditDataSourceModal from "../components/datasources/EditDataSourceModal";
import DeleteDataSourceDialog from "../components/datasources/DeleteDataSourceDialog";
import PaginatedTable from "../components/ui/paginated-table";
import GuidedChooser, { startGuided } from "../components/guided/GuidedChooser";
import { useTutorialDemo } from "../components/tutorial/useTutorialDemo";
import { PolicyModal } from "../components/policies/AccessPoliciesPanel";

export default function DataSources() {
  const [sources, setSources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  // Tutorial demo instructions: open the create-source modal (plain or
  // AI-built variant) or the access-policy modal for DISPLAY — fictional
  // sample values, submit disabled, no dependency on any existing source.
  const demo = useTutorialDemo(["new-source", "new-source-agentic", "add-policy"]);
  const [isChooserOpen, setIsChooserOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [inspectTarget, setInspectTarget] = useState(null); // a write source (data_target) to inspect
  const [connectors, setConnectors] = useState([]);
  // { kind, from } — `from` records WHICH chooser screen launched this so Back
  // returns to that exact screen ('picker' = the type grid, 'chooser' = the
  // Manual/Guided/Build list). null when opened directly, and then no Back shows.
  const [requestDialog, setRequestDialog] = useState(null);
  const [chooserPickingType, setChooserPickingType] = useState(false);
  // In-flight connector builds. A new_source request delivers a source TYPE —
  // a new entry in the create modal's type list — so until it lands it leaves
  // NO trace on this page at all. Without this strip, filing one looks like
  // nothing happened. Other rails get away with a bare count badge because
  // their deliverable (an app, a report) is at least a thing you asked for by
  // name; a connector type is invisible by construction.
  const [openSourceRequests, setOpenSourceRequests] = useState([]);
  // Connectors offered on the "Change a data source" tab — see sourceRequestKinds.
  // Kinds this project's own sources run on, plus anything agent-built. Ordered
  // by the catalog so the list reads the same as the type picker.
  const changeableConnectors = useMemo(() => {
    const inUse = new Set((sources || []).map((s) => s.kind || s.source_type).filter(Boolean));
    return (connectors || []).filter(
      (c) => c.adapter_archetype || inUse.has(c.storage_type_out) || inUse.has(c.storage_type_in)
    );
  }, [sources, connectors]);
  const navigate = useNavigate();

  useEffect(() => { loadSources(); }, []);
  useEffect(() => {
    let alive = true;
    const load = () => listAppRequests({ status: "open" })
      .then((r) => {
        if (!alive) return;
        setOpenSourceRequests((r?.requests || [])
          .filter((x) => x.kind === "new_source" || x.kind === "change_source"));
      })
      .catch(() => {});
    load();
    // Same broadcast the table listens to, so completing a build clears the strip.
    window.addEventListener("cortex:data-changed", load);
    return () => { alive = false; window.removeEventListener("cortex:data-changed", load); };
  }, []);
  // Guided types come from the connector catalog (was a hardcoded list that
  // drifted). Agentic kinds are excluded — they have their own masters in the
  // Create modal instead of the chat-guided flow.
  useEffect(() => {
    getNodeCatalog().then(cat => setConnectors((cat?.connectors || []).filter(c => c.role === 'load'))).catch(() => {});
  }, []);

  // Live refresh: re-fetch when the Guided demo (or any background mutation)
  // broadcasts cortex:data-changed, so the page shows progress as it happens.
  useEffect(() => {
    const onChanged = () => loadSources({ quiet: true });
    window.addEventListener("cortex:data-changed", onChanged);
    return () => window.removeEventListener("cortex:data-changed", onChanged);
  }, []);

  // `quiet` = background live-refresh: swap data in without flipping the full-page
  // loading state, so the table doesn't blink every few seconds during a guided run.
  const loadSources = async ({ quiet = false } = {}) => {
    if (!quiet) setIsLoading(true);
    try {
      // Unified list: data_source (read) + data_target (write) as one Sources list.
      const r = await listSources();
      setSources(r?.sources || []);
    } catch (error) {
      console.error("Error loading data sources:", error);
    } finally {
      if (!quiet) setIsLoading(false);
    }
  };

  const handleSuccess = () => {
    loadSources();
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteDialogOpen(false);
    setSelectedSource(null);
  };

  const openEditModal = (source) => { setSelectedSource(source); setIsEditModalOpen(true); };
  const openDeleteDialog = (source) => { setSelectedSource(source); setIsDeleteDialogOpen(true); };

  // A write source (data_target) has no detail PAGE, but it does have live Schema +
  // Records via the target endpoints — open the same inspector the pipeline canvas uses.
  const openTarget = (source) => {
    const type = source.kind || source.source_type;
    const eid = source.entity_id ?? source.id;
    setInspectTarget({
      name: source.source_name,
      storage_type: type,
      storage_id: `${type}:${eid}`,
      entity_id: eid,
      status: source.status,
      origin: source.origin, // 'data_source' | 'data_target' — drives the config route
    });
  };

  const activeSources = sources.filter(s => s.status === 'Active').length;
  const goldenSources = sources.filter(s => s.is_golden_source).length;

  return (
    <div data-tour="page-data-sources" className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">

      {/* Title + Action */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[32px] font-medium leading-tight text-page-title">Data Sources</h1>
          <p className="text-sm font-normal text-page-muted">Manage all external and internal source systems</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-1">
          {/* ONE create entry point. Building a new connector TYPE is a route
              INSIDE the chooser, not a second button competing with it — the
              user learns their system is missing while scanning the type list. */}
          <button
            data-tour="new-source"
            onClick={() => setIsChooserOpen(true)}
            className="flex items-center gap-2 w-[183px] h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors"
          >
            <PlusCircle size={24} />
            New Source
          </button>
        </div>
      </div>

      {/* Connector builds in flight — the only trace a new_source request has
          on this page until the type ships. Links to the Board, where the
          request's live log is. */}
      {openSourceRequests.length > 0 && (
        <button
          type="button"
          data-testid="source-requests-strip"
          onClick={() => navigate(createPageUrl("board"))}
          className="flex items-center gap-2.5 text-left w-full px-4 py-2.5 rounded-[6px] bg-card-bg border border-page-divider hover:border-nav-active transition-colors cursor-pointer"
        >
          <Loader2 size={15} className="shrink-0 animate-spin text-nav-active" />
          <span className="text-sm text-page-text">
            {openSourceRequests.length === 1
              ? <>Building connector <span className="font-medium">{openSourceRequests[0].title}</span></>
              : <>{openSourceRequests.length} connectors building</>}
          </span>
          <span className="ml-auto text-xs text-page-muted">
            {openSourceRequests.length === 1 ? openSourceRequests[0].status : "View on Board"}
          </span>
        </button>
      )}

      {/* Stats Cards */}
      <div className="grid gap-5 grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
        <div className="bg-card-bg rounded-[5px] h-[83px] px-5 py-3 flex items-start gap-3">
          <Server size={24} color="#377AD6" className="shrink-0" />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium" style={{ color: 'var(--page-text)' }}>Total Sources</span>
            <span className="text-[30px] font-medium leading-none" style={{ color: 'var(--page-text)' }}>{sources.length}</span>
          </div>
        </div>
        <div className="bg-card-bg rounded-[5px] h-[83px] px-5 py-3 flex items-start gap-3">
          <Database size={24} color="rgba(77,179,0,0.9)" className="shrink-0" />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium" style={{ color: 'var(--page-text)' }}>Active Sources</span>
            <span className="text-[30px] font-medium leading-none" style={{ color: 'var(--page-text)' }}>{activeSources}</span>
          </div>
        </div>
        <div className="bg-card-bg rounded-[5px] h-[83px] px-5 py-3 flex items-start gap-3">
          <LinkIcon size={24} color="rgba(233,171,48,0.9)" className="shrink-0" />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium" style={{ color: 'var(--page-text)' }}>Golden Sources</span>
            <span className="text-[30px] font-medium leading-none" style={{ color: 'var(--page-text)' }}>{goldenSources}</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <PaginatedTable
        data={sources}
        externalLoading={isLoading}
        renderTable={(paginatedData, loading) => (
          <div className="bg-card-bg rounded-[8px] overflow-x-auto">
            <DataSourcesTable
              sources={paginatedData}
              isLoading={loading}
              onEdit={openEditModal}
              onDelete={openDeleteDialog}
              onOpenTarget={openTarget}
            />
          </div>
        )}
      />

      {isChooserOpen && (
        <GuidedChooser
          initialPickingType={chooserPickingType}
          title="New data source"
          manualLabel="Manual"
          manualHint="Fill in the connection form yourself — or pick an AI-built source (External API / Web Scraper / AI Enrichment / News & Feeds / Knowledge Base) and let its master configure it."
          guidedHint="Let the assistant register a source from a plain-language description and discover its schema, step by step."
          guidedTypes={
            connectors.filter(c => c.family !== 'agentic').length
              ? connectors.filter(c => c.family !== 'agentic').map(c => ({
                  value: c.storage_type_out,
                  label: (c.label || c.storage_type_out).replace(/^Load\s+/, ''),
                }))
              : [
                  { value: "file", label: "File (CSV/XLSX)" },
                  { value: "postgres", label: "Postgres" },
                  { value: "mongo", label: "Mongo" },
                ]
          }
          extraOption={{
            label: "Build a new connector",
            hint: "The system you need isn't in the list — describe it and an agent researches it, builds the connection form, and proves it with a live read.",
            icon: Plug,
            pickerLabel: "Don't see your system? Build a connector",
            // `from` is captured at the click so Back reopens the SAME screen.
            onSelect: (fromPicker) => {
              setChooserPickingType(!!fromPicker);
              setIsChooserOpen(false);
              setRequestDialog({ kind: "new_source", from: "chooser" });
            },
          }}
          onManual={() => { setIsChooserOpen(false); setIsCreateModalOpen(true); }}
          onGuided={(kind) => { setIsChooserOpen(false); startGuided("data_source", kind); }}
          onClose={() => setIsChooserOpen(false)}
        />
      )}

      {/* Tutorial demo modals — display-only, never submit. */}
      {(demo === "new-source" || demo === "new-source-agentic") && (
        <CreateDataSourceModal
          isOpen
          tutorialDemo={demo === "new-source-agentic" ? "agentic" : true}
          onClose={() => {}}
          onSuccess={() => {}}
        />
      )}
      {demo === "add-policy" && (
        <PolicyModal tutorialDemo dataSourceId={null} policy={null} onClose={() => {}} onSaved={() => {}} />
      )}

      <CreateDataSourceModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleSuccess}
      />

      {selectedSource && (
        <EditDataSourceModal
          isOpen={isEditModalOpen}
          onClose={() => { setIsEditModalOpen(false); setSelectedSource(null); }}
          onSuccess={handleSuccess}
          source={selectedSource}
        />
      )}

      {selectedSource && (
        <DeleteDataSourceDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => { setIsDeleteDialogOpen(false); setSelectedSource(null); }}
          onSuccess={handleSuccess}
          source={selectedSource}
        />
      )}

      {inspectTarget && (
        <StorageInspectModal storage={inspectTarget} onClose={() => setInspectTarget(null)} />
      )}

      {requestDialog && (
        <AppRequestDialog
          kinds={sourceRequestKinds(changeableConnectors)}
          title="Build a data source"
          preset={{ kind: requestDialog.kind }}
          onBack={requestDialog.from === "chooser"
            ? () => { setRequestDialog(null); setIsChooserOpen(true); }
            : null}
          onClose={() => setRequestDialog(null)}
          onCreated={() => { setRequestDialog(null); navigate(createPageUrl("board")); }}
        />
      )}
    </div>
  );
}
